import { memo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { IconButton, InputAdornment, styled } from '@mui/material';
import type { InputBaseProps } from '@mui/material/InputBase';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'utils/hooks';
import { TextField, type Props as TextFieldProps } from 'components/mui/common/TextField';

const Wrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '8px',
      borderColor: theme.palette.grey[300],
      borderWidth: '1px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 10px',
    fontSize: '13px',
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    fontSize: '18px',
    color: theme.palette.text.secondary,
  },
}));

export const SearchField = memo(
  ({
    value: valueProp,
    onChange,
    maxWidth,
    autoFocus = false,
    dataTestId,
    clearable = false,
    slotProps,
    ...rest
  }: TextFieldProps & { value?: string; maxWidth?: string; autoFocus?: boolean; dataTestId?: string; clearable?: boolean }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(valueProp ?? '');
    const debouncedValue = useDebounce<string>(value, 500);
    const lastEmittedValueRef = useRef<string | undefined>(undefined);
    const onChangeRef = useRef(onChange);

    useLayoutEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    }, []);

    const handleClear = useCallback(() => {
      setValue('');
      lastEmittedValueRef.current = '';
      onChangeRef.current?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
    }, []);

    useEffect(() => {
      const next = valueProp ?? '';
      setValue((current) => (current !== next ? next : current));
    }, [valueProp]);

    useEffect(() => {
      if (lastEmittedValueRef.current === debouncedValue) {
        return;
      }

      lastEmittedValueRef.current = debouncedValue;
      onChangeRef.current?.({ target: { value: debouncedValue } } as ChangeEvent<HTMLInputElement>);
    }, [debouncedValue]);

    const showClear = Boolean(clearable && value && !rest.disabled);

    return (
      <Wrapper>
        <TextField
          dataTestId={dataTestId ?? 'search-field'}
          autoFocus={autoFocus}
          size="small"
          value={value}
          onChange={handleChange}
          {...rest}
          slotProps={{
            ...slotProps,
            input: (ownerState) => {
              const inputSlotProps = typeof slotProps?.input === 'function' ? slotProps.input(ownerState) : slotProps?.input;
              const callerEndAdornment = (inputSlotProps as InputBaseProps | undefined)?.endAdornment;
              const suffixAdornment = rest.suffix?.length ? <InputAdornment position="end">{rest.suffix}</InputAdornment> : undefined;
              const trailingAdornment = callerEndAdornment ?? suffixAdornment;

              return {
                ...inputSlotProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                ...(showClear
                  ? {
                      endAdornment: (
                        <>
                          <InputAdornment position="end">
                            <IconButton
                              type="button"
                              size="small"
                              edge="end"
                              aria-label={t('common.clear')}
                              onClick={handleClear}
                              disabled={rest.disabled}>
                              <CloseIcon />
                            </IconButton>
                          </InputAdornment>
                          {trailingAdornment}
                        </>
                      ),
                    }
                  : {}),
              };
            },
          }}
          fullWidth
          disableHelperText
        />
      </Wrapper>
    );
  }
);
